// src/features/media/components/TimecodeInput.tsx

import { useEffect, useState } from 'react';
import { TextInput } from '@mantine/core';
import { MediaUtils } from '../MediaUtils';

interface TimecodeInputProps {
  value: number; // Committed seconds
  min: number;
  max: number;
  ariaLabel: string;
  onCommit: (seconds: number) => void;
}

/**
 * Compact editable timecode field for clip in/out points. The draft text
 * commits on Enter or blur — never mid-typing — clamped to [min, max];
 * unparseable text reverts to the last committed value. Arrow up/down
 * nudge one second.
 */
export function TimecodeInput({
  value,
  min,
  max,
  ariaLabel,
  onCommit,
}: TimecodeInputProps) {
  const [draft, setDraft] = useState(() => MediaUtils.formatTimecode(value));

  // External changes (slider drag, Set buttons, I/O keys) refresh the draft.
  useEffect(() => {
    setDraft(MediaUtils.formatTimecode(value));
  }, [value]);

  const commit = (seconds: number | null) => {
    if (seconds === null) {
      setDraft(MediaUtils.formatTimecode(value));
      return;
    }
    const clamped = Math.min(max, Math.max(min, seconds));
    setDraft(MediaUtils.formatTimecode(clamped));
    // Only real changes commit — a focus/blur pass or re-typing the current
    // value must not fire onCommit side effects (the preview seeks on commit).
    if (clamped !== value) {
      onCommit(clamped);
    }
  };

  const nudge = (delta: number) => {
    const base = MediaUtils.parseTimecode(draft) ?? value;
    commit(base + delta);
  };

  return (
    <TextInput
      size="xs"
      w={90}
      aria-label={ariaLabel}
      placeholder="00:00"
      value={draft}
      onChange={(e) => setDraft(e.currentTarget.value)}
      onBlur={() => commit(MediaUtils.parseTimecode(draft))}
      onKeyDown={(e) => {
        if (e.key === 'Enter') {
          e.preventDefault();
          commit(MediaUtils.parseTimecode(draft));
        } else if (e.key === 'ArrowUp') {
          e.preventDefault();
          nudge(1);
        } else if (e.key === 'ArrowDown') {
          e.preventDefault();
          nudge(-1);
        }
      }}
      styles={{
        input: {
          fontFamily: 'var(--mantine-font-family-monospace)',
          textAlign: 'center',
        },
      }}
    />
  );
}
